import type { AnonymizationMode } from '../types/api';

export const DEFAULT_MASK_CHAR = '*';

/** Pre-selected mode. Mirrors DEFAULT_MODE in app/core/masking.py:
 *  removing the data is the safer thing to do by default. */
export const DEFAULT_MODE: AnonymizationMode = 'redact';

/** Characters offered in the UI for mask/redact. Anything the backend
 *  accepts (a single non-blank char) works; these are the useful ones. */
export const MASK_CHAR_OPTIONS = ['*', '#', 'X', '•', '█'];

export interface ModeInfo {
  value: AnonymizationMode;
  label: string;
  description: string;
  /** Shown as a badge — true means the tenant key can still map back. */
  reversible: boolean;
}

export const MODES: ModeInfo[] = [
  {
    value: 'pseudonymize',
    label: 'Pseudonimizar',
    description:
      'Troca por dados fictícios válidos e consistentes. O mesmo CPF vira sempre o mesmo CPF falso, preservando a integridade referencial do documento.',
    reversible: true,
  },
  {
    value: 'mask',
    label: 'Mascarar',
    description:
      'Oculta parte do dado e mantém alguns caracteres para conferência humana. Não há como recuperar o valor original.',
    reversible: false,
  },
  {
    value: 'redact',
    label: 'Ocultar',
    description:
      'Substitui todos os caracteres pelo símbolo escolhido, preservando a pontuação — fica claro que havia um dado sensível ali.',
    reversible: false,
  },
  {
    value: 'label',
    label: 'Rotular',
    description:
      'Troca o dado pelo nome da categoria, como [CPF] ou [NOME]. A opção mais legível para documentos que serão publicados.',
    reversible: false,
  },
  {
    value: 'blackout',
    label: 'Tarjar',
    description:
      'Cobre o dado com uma tarja preta, como num documento censurado. No PDF vira um retângulo preto; nos arquivos Office, uma barra de █.',
    reversible: false,
  },
];

const SAMPLE = '123.456.789-01';

/** Before → after preview for the mode cards. Mirrors the backend
 *  rules in app/core/masking.py for a CPF. */
export function getModeExample(mode: AnonymizationMode, maskChar: string): string {
  const c = maskChar || DEFAULT_MASK_CHAR;
  switch (mode) {
    case 'pseudonymize':
      return '874.201.336-58';
    case 'mask':
      return `${c.repeat(3)}.456.789-${c.repeat(2)}`;
    case 'redact':
      return `${c.repeat(3)}.${c.repeat(3)}.${c.repeat(3)}-${c.repeat(2)}`;
    case 'label':
      return '[CPF]';
    case 'blackout':
      return '█'.repeat(SAMPLE.length);
  }
}

export const MODE_SAMPLE_INPUT = SAMPLE;

/** Verb used in the review/done copy, e.g. "3 ocorrências serão mascaradas". */
export const MODE_VERB: Record<AnonymizationMode, string> = {
  pseudonymize: 'pseudonimizadas',
  mask: 'mascaradas',
  redact: 'ocultadas',
  label: 'rotuladas',
  blackout: 'tarjadas',
};
